import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';

const useHistoryScrollPosition = (timeInterval = 50, retryLimit = 50): void => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const stopListening = history.listen(({ key }, type: string) => {
      // Save a scroll position with history key of the page that user navigates from
      window.sessionStorage.setItem(
        `historical-scroll-${location.key || 'initial'}`,
        String(window.scrollY)
      );

      let lastTimestamp = 0;
      let lastHeight = 0;
      let counter = 0;

      // Get scroll position of the page that users navigates to
      const scrollPosition =
        Number(window.sessionStorage.getItem(`historical-scroll-${key || 'initial'}`)) || 0;

      function scrollToPosition(timestamp?: number) {
        const requestAnimationFrameId = requestAnimationFrame(scrollToPosition);

        // Try to scroll to position every `timeInterval` (50ms - default value)
        if (!lastTimestamp || (timestamp && timestamp - lastTimestamp >= timeInterval)) {
          lastTimestamp = timestamp || 0;
          lastHeight = document.body.clientHeight;

          // If the page has the height that does not exceed the desired scroll position,
          // try to scroll to that position until the number of retries is bigger than `retryLimit` (50 - default value)
          if (document.body.clientHeight < scrollPosition) {
            window.scrollTo(0, scrollPosition);

            // If reached to the end of the page (page height doesn't change),
            // try few more times - `retryLimit` (50 - default value), as the content
            // might be lazy loaded or it might have changed since the last visit on the page
            if (lastHeight === document.body.clientHeight) {
              counter++;
              if (counter >= retryLimit) {
                cancelAnimationFrame(requestAnimationFrameId);
              }
            } else {
              lastHeight = document.body.clientHeight;
            }
            // If the page has the height that exceeds the desired scroll position,
            // scroll to that position and stop the interval
          } else if (document.body.clientHeight >= scrollPosition) {
            window.scrollTo(0, scrollPosition);
            cancelAnimationFrame(requestAnimationFrameId);
          }
        }
      }

      const isHashOnlyChange =
        history.location.hash && history.location.pathname === location.pathname;

      // If navigating to a new page and not to part of the current page
      if (type === 'PUSH' && !isHashOnlyChange) {
        window.scrollTo(0, 0);

        // If navigating with 'back' or 'forward' browser buttons
      } else if (type === 'POP') {
        scrollToPosition();
      }
    });
    return () => stopListening();
  }, [location, history, retryLimit, timeInterval]);
};

export default useHistoryScrollPosition;
